/**
 * Yaariplay — daily word game data layer (Phase 1 MVP).
 *
 * Mirrors the shape of `useEvents.ts`. TanStack Query hooks for reads,
 * mutations for writes; keys registered under `queryKeys.yaariplay`. The
 * server owns the puzzle answer — it NEVER surfaces client-side until the
 * user solves the puzzle OR uses all 6 guesses.
 *
 * Endpoint mapping:
 *   • useYaariplayToday        → GET  /api/yaariplay/today          (auth)
 *   • useYaariplayGuess        → POST /api/yaariplay/guess          (auth)
 *   • useYaariplayStats        → GET  /api/yaariplay/today/stats    (public)
 *   • useYaariplayLeaderboard  → GET  /api/yaariplay/leaderboard    (public)
 *   • useYaariplayStreak       → GET  /api/yaariplay/streak         (auth)
 *   • useYaariplayHistory      → GET  /api/yaariplay/history        (auth)
 *   • useYaariplayPrefs        → PATCH /api/yaariplay/prefs         (auth)
 *
 * Public endpoints (`stats`, `leaderboard`) are always enabled and safe for
 * anonymous visitors — the widget on the marketing homepage relies on them.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuth } from '@/pages/auth';
import type {
  YaariplayFriendsTodayResult,
  YaariplayGuessResult,
  YaariplayHistoryResult,
  YaariplayLeaderboard,
  YaariplayNudgeResult,
  YaariplayPrefsInput,
  YaariplayPrefsResult,
  YaariplayStreakResult,
  YaariplayTodayResult,
  YaariplayTodayStats,
} from '@/types/api';

/** Serialise an object of query params into a `?a=1&b=2` string, skipping
 *  nulls/empties. Kept local to avoid pulling in the events helper. */
function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    search.set(k, String(v));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** GET /api/yaariplay/today — auth. Fetches puzzle metadata + viewer state. */
export function useYaariplayToday() {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: queryKeys.yaariplay.today(),
    enabled: Boolean(token),
    // Today's puzzle rolls at midnight IST. Once the client has it, we don't
    // need to poll — the play state changes only via useYaariplayGuess (which
    // invalidates this key on success). 5 min stale is comfortable.
    staleTime: 5 * 60_000,
    queryFn: () => api.get<YaariplayTodayResult>('yaariplay/today'),
  });
}

/** GET /api/yaariplay/today/stats — PUBLIC. Widget-friendly aggregates. */
export function useYaariplayStats() {
  return useQuery({
    queryKey: queryKeys.yaariplay.stats(),
    // Server caches for 60s; 60s stale here matches — avoids a duplicate
    // fetch on every widget mount inside the same minute.
    staleTime: 60_000,
    queryFn: () => api.get<YaariplayTodayStats>('yaariplay/today/stats'),
  });
}

/** GET /api/yaariplay/leaderboard — PUBLIC. `date` defaults to today. */
export interface YaariplayLeaderboardParams {
  date?: string;
  limit?: number;
  scope?: 'day' | 'week' | 'alltime';
}
export function useYaariplayLeaderboard(params: YaariplayLeaderboardParams = {}) {
  return useQuery({
    queryKey: queryKeys.yaariplay.leaderboard(params),
    staleTime: 60_000,
    queryFn: () =>
      api.get<YaariplayLeaderboard>(`yaariplay/leaderboard${toQuery(params as Record<string, unknown>)}`),
  });
}

/** GET /api/yaariplay/streak — auth. */
export function useYaariplayStreak() {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: queryKeys.yaariplay.streak(),
    enabled: Boolean(token),
    staleTime: 60_000,
    queryFn: () => api.get<YaariplayStreakResult>('yaariplay/streak').then(r => r.streak),
  });
}

/** GET /api/yaariplay/history — auth. Last 30 plays. */
export function useYaariplayHistory() {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: queryKeys.yaariplay.history(),
    enabled: Boolean(token),
    staleTime: 5 * 60_000,
    queryFn: () => api.get<YaariplayHistoryResult>('yaariplay/history').then(r => r.history),
  });
}

/** POST /api/yaariplay/guess — auth. Body: `{ word }`. Invalidates today +
 *  streak on solve. */
export function useYaariplayGuess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (word: string) =>
      api.post<YaariplayGuessResult>('yaariplay/guess', { word }),
    onSuccess: (result) => {
      // Freshen every cache that reflects the new play state.
      qc.invalidateQueries({ queryKey: queryKeys.yaariplay.today() });
      qc.invalidateQueries({ queryKey: queryKeys.yaariplay.stats() });
      qc.invalidateQueries({ queryKey: queryKeys.yaariplay.leaderboard() });
      // Streak only shifts on a solve or a fail (guess_count reached max);
      // an in-progress guess doesn't move it, but the extra invalidation is
      // cheap and keeps the code path simple.
      qc.invalidateQueries({ queryKey: queryKeys.yaariplay.streak() });
      // Return the result so callers can branch on it (e.g. show the
      // post-game modal).
      return result;
    },
  });
}

/** PATCH /api/yaariplay/prefs — auth. Toggles opt-out flags. */
export function useYaariplayPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: YaariplayPrefsInput) =>
      api
        .patch<YaariplayPrefsResult>('yaariplay/prefs', patch)
        .then(r => r.prefs),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.yaariplay.leaderboard() });
      qc.invalidateQueries({ queryKey: queryKeys.yaariplay.stats() });
      qc.invalidateQueries({ queryKey: queryKeys.yaariplay.today() });
    },
  });
}

/** GET /api/yaariplay/friends/today — auth. Lists viewer's friends and whether
 *  each has played today. Powers the right-rail "Friends playing today"
 *  widget + the Nudge button. */
export function useYaariplayFriendsToday() {
  const token = useAuth((s) => s.token);
  return useQuery({
    queryKey: queryKeys.yaariplay.friendsToday(),
    enabled: Boolean(token),
    // Friends' play status moves at the same cadence as /today — short stale
    // keeps the Nudge/✅ labels current without hammering the server.
    staleTime: 60_000,
    queryFn: () => api.get<YaariplayFriendsTodayResult>('yaariplay/friends/today'),
  });
}

/** POST /api/yaariplay/nudge/:userId — auth. Fire a chat message to a friend
 *  nudging them to play today's puzzle. Invalidates the friends-today key
 *  on success so the Nudge button doesn't re-trigger. */
export function useYaariplayNudge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) =>
      api.post<YaariplayNudgeResult>(`yaariplay/nudge/${userId}`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.yaariplay.friendsToday() });
    },
  });
}
